import { Injectable } from '@angular/core';
import { AppEnablementStatus, PartnerAppEnablementStatus } from '@vendasta/marketplace-packages';
import { ResellerItem } from '@vendasta/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MarketplaceApiService } from '../marketplace.api.service';
import { SortDescriptor } from '../sort';
import {
  convertAddonToResellerItem,
  convertAppToResellerItem,
  convertCustomProductToResellerItem,
  convertProductToResellerItem,
  makeResellerMap,
} from './conversion';
import { ResellerItemMap } from './reseller-item-map';
import {
  arrayDefined,
  getActivatableAppIdsFromActivatableApps,
  getApprovedResellerItems,
  inActivatableList,
  mergeAppData,
  sortResellerItems,
} from './utils';

interface NestedResellerItem extends ResellerItem {
  addons: ResellerItem[];
}

@Injectable({ providedIn: 'root' })
export class ResellerItemService {
  private readonly apps$: Observable<ResellerItem[]>;
  private readonly appsForSelectedMarket$: Observable<ResellerItem[]>;
  private readonly addons$: Observable<ResellerItem[]>;
  private readonly products$: Observable<ResellerItem[]>;
  private readonly productsForMarket$: Observable<ResellerItem[]>;
  private readonly customProducts$: Observable<ResellerItem[]>;
  private readonly resellerItemMap$: Observable<ResellerItemMap>;
  private readonly activatableAppIds$: Observable<string[]>;
  private readonly activatablePartnerData$: Observable<ResellerItem[]>;
  private readonly activatableMarketData$: Observable<ResellerItem[]>;
  private readonly activatableNestedPartnerData$: Observable<NestedResellerItem[]>;
  public isProductLoading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  constructor(private marketplaceApiService: MarketplaceApiService) {
    this.apps$ = this.marketplaceApiService.apps().pipe(
      map((apps) => {
        if (!arrayDefined(apps)) {
          return [];
        }
        return apps.map((app) => convertAppToResellerItem(app));
      }),
      shareReplay(1),
    );

    this.appsForSelectedMarket$ = this.marketplaceApiService.appsForSelectedMarket$.pipe(
      map((apps) => {
        if (!arrayDefined(apps)) {
          return [];
        }
        return apps.map((app) => convertAppToResellerItem(app));
      }),
      shareReplay(1),
    );

    this.addons$ = combineLatest([this.marketplaceApiService.addons$, this.apps$]).pipe(
      map(([addons, apps]) => {
        if (!arrayDefined(addons)) {
          return [];
        }

        const appMap: Map<string, ResellerItem> = new Map();
        if (arrayDefined(apps)) {
          apps.forEach((app) => appMap.set(app.appId, app));
        }

        return addons.map((addon) => {
          const parentApp = appMap.get(addon.appId);
          return convertAddonToResellerItem(addon, parentApp);
        });
      }),
      shareReplay(1),
    );

    this.products$ = this.marketplaceApiService.products().pipe(
      map((products) => {
        this.isProductLoading$$.next(true);
        if (!arrayDefined(products)) {
          return [];
        }
        return products.map((product) => convertProductToResellerItem(product));
      }),
      shareReplay(1),
    );

    this.productsForMarket$ = this.marketplaceApiService.productsForMarket().pipe(
      map((products) => (products || []).map((product) => convertProductToResellerItem(product))),
      shareReplay(1),
    );

    this.customProducts$ = this.marketplaceApiService.customProducts().pipe(
      map((cps) => {
        if (!arrayDefined(cps)) {
          return [];
        }
        return cps.map((cp) => convertCustomProductToResellerItem(cp));
      }),
      shareReplay(1),
    );

    this.resellerItemMap$ = combineLatest([this.apps$, this.addons$, this.products$, this.customProducts$]).pipe(
      map(([apps, addons, products, cps]) => makeResellerMap(products, cps, addons, apps)),
      shareReplay(1),
    );

    this.activatableAppIds$ = this.marketplaceApiService.activatableApps().pipe(
      map((apps) => {
        if (!arrayDefined(apps)) {
          return [];
        }
        return getActivatableAppIdsFromActivatableApps(apps);
      }),
    );

    this.activatablePartnerData$ = combineLatest([this.getPartnerData(), this.activatableAppIds$, this.apps$]).pipe(
      map(([resellerItems, activatableApps, marketplaceApps]) => {
        const mergedData = mergeAppData(resellerItems, marketplaceApps);
        return this.sortViewableResellerItems(mergedData, activatableApps);
      }),
      shareReplay(1),
    );

    this.activatableMarketData$ = combineLatest([this.getMarketData(), this.activatableAppIds$, this.apps$]).pipe(
      map(([resellerItems, activatableApps, marketplaceApps]) => {
        const mergedData = mergeAppData(resellerItems, marketplaceApps);
        return this.sortViewableResellerItems(mergedData, activatableApps);
      }),
      shareReplay(1),
    );

    this.activatableNestedPartnerData$ = combineLatest([
      this.getPartnerData(),
      this.activatableAppIds$,
      this.apps$,
    ]).pipe(
      map(([resellerItems, activatableApps, marketplaceApps]) => {
        const mergedData = mergeAppData(resellerItems, marketplaceApps);
        return this.nestActivatableResellerItemsWithoutSuspended(mergedData, activatableApps);
      }),
      shareReplay(1),
    );
  }

  // Use this method to combine custom products and apps going forwards. It prioritizes the app reseller item over the custom product,
  // just incase there is one of each with the same id.
  static combineCustomProductsAndApps(cps: ResellerItem[], apps: ResellerItem[]): ResellerItem[] {
    const combinedList = (cps || []).concat(apps || []);

    const deduplicatedMap = (combinedList || []).reduce((m, app) => {
      m.set(app.appId, app);
      return m;
    }, new Map());

    const deduplicatedList = [];
    deduplicatedMap.forEach((app) => deduplicatedList.push(app));
    return deduplicatedList;
  }

  partnerAppEnablementStatusesMap(): Observable<{
    [appId: string]: AppEnablementStatus;
  }> {
    return this.marketplaceApiService.paesMap$;
  }

  partnerAppEnablementStatuses(): Observable<PartnerAppEnablementStatus[]> {
    return this.marketplaceApiService.paes$;
  }

  getResellerMap(): Observable<ResellerItemMap> {
    return this.resellerItemMap$;
  }

  getAppData(): Observable<ResellerItem[]> {
    return this.apps$;
  }

  getAppsForSelectedMarket(): Observable<ResellerItem[]> {
    return this.appsForSelectedMarket$;
  }

  getCustomProductData(): Observable<ResellerItem[]> {
    return this.customProducts$;
  }

  getProductData(): Observable<ResellerItem[]> {
    return this.products$;
  }

  getAddonData(): Observable<ResellerItem[]> {
    return this.addons$;
  }

  getActivatablePartnerData(): Observable<ResellerItem[]> {
    return this.activatablePartnerData$;
  }

  getActivatablePartnerDataWithoutSuspended(): Observable<ResellerItem[]> {
    return this.activatablePartnerData$.pipe(map((resellerItems) => resellerItems.filter((item) => !item.suspended)));
  }

  getActivatableMarketDataWithoutSuspended(): Observable<ResellerItem[]> {
    return this.activatableMarketData$.pipe(map((resellerItems) => resellerItems.filter((item) => !item.suspended)));
  }

  getActivatableNestedPartnerDataWithoutSuspended(): Observable<NestedResellerItem[]> {
    return this.activatableNestedPartnerData$;
  }

  getPartnerData(): Observable<ResellerItem[]> {
    return combineLatest([this.getProductData(), this.getAddonData(), this.apps$]).pipe(
      map(([ri1, ri2, apps]) => {
        return mergeAppData(getApprovedResellerItems(ri1, ri2), apps);
      }),
    );
  }

  getMarketData(): Observable<ResellerItem[]> {
    return combineLatest([this.productsForMarket$, this.getAddonData(), this.apps$]).pipe(
      map(([ri1, ri2, apps]) => {
        return mergeAppData(getApprovedResellerItems(ri1, ri2), apps);
      }),
    );
  }

  sort(items: ResellerItem[], fieldname: string, sortDescriptor: SortDescriptor): ResellerItem[] {
    return sortResellerItems(items, fieldname, sortDescriptor);
  }

  nestActivatableResellerItemsWithoutSuspended(
    resellerItems: ResellerItem[],
    activatableApps: string[],
  ): NestedResellerItem[] {
    const activatableItems: NestedResellerItem[] = [];
    for (const currItem of resellerItems) {
      if (currItem.addonId) {
        const index = activatableItems.findIndex((item) => item.appId && item.appId === currItem.appId);
        if (!currItem.suspended && inActivatableList(activatableApps, currItem.appId, currItem.addonId)) {
          if (!activatableItems[index].addons) activatableItems[index].addons = [];
          activatableItems[index].addons.push(currItem);
        }
      } else {
        if (!currItem.suspended && inActivatableList(activatableApps, currItem.appId, currItem.addonId))
          activatableItems.push(currItem as NestedResellerItem);
      }
    }
    return activatableItems;
  }

  sortViewableResellerItems(resellerItems: ResellerItem[], activatableAppIDs: string[]): ResellerItem[] {
    const viewableItems: ResellerItem[] = [];

    const activatableResellerItems = resellerItems.filter((resellerItem) => {
      return inActivatableList(activatableAppIDs, resellerItem.appId, resellerItem.addonId);
    });

    for (const currItem of activatableResellerItems) {
      if (currItem.addonId) {
        viewableItems.splice(
          viewableItems.findIndex((item) => item.appId && item.appId === currItem.appId) + 1,
          0,
          currItem,
        );
      } else {
        viewableItems.push(currItem);
      }
    }
    return viewableItems;
  }

  refreshMarketplaceData(): void {
    this.marketplaceApiService.refreshMarketplaceData();
  }
}
